import { useMemo, useReducer } from 'react'

import type { FormModuleComponent } from 'services/content/components/types/FormModule'

const CHANGE = 'change'

type State = {
  values: Record<string, string>
  required: string[]
}

type Action = {
  type: typeof CHANGE
  payload: { name: string; value: string }
}

function reducer(state: State, action: Action) {
  switch (action.type) {
    case CHANGE: {
      const { name, value } = action.payload
      return {
        ...state,
        values: {
          ...state.values,
          [name]: value,
        },
      }
    }
    default:
      return state
  }
}

function useFormFields(components: FormModuleComponent[]) {
  const defaultState = useMemo(() => {
    return components.reduce(
      (acc: State, component) => {
        switch (component.name) {
          case 'Input': {
            const { fieldId, isRequired } = component.props
            acc.values[fieldId] = ''

            if (isRequired) {
              acc.required.push(fieldId)
            }
          }
        }
        return acc
      },
      { values: {}, required: [] },
    )
  }, [components])

  const [state, dispatch] = useReducer(reducer, defaultState)

  function handleChange(name: string, value: string) {
    dispatch({
      type: CHANGE,
      payload: { name, value },
    })
  }

  const isValid = useMemo(() => {
    const isMissingRequired = state.required.find(
      (field) => !state.values[field] || state.values[field] === '',
    )

    return !isMissingRequired
  }, [state])

  return {
    values: state.values,
    isValid,
    onChange: handleChange,
  }
}

export default useFormFields
